/** The stylesheet, as a string.
 *
 * It lives in TypeScript rather than a `.css` file for one reason: the ui
 * package is consumed by a bundler, by `tsc -b`, and by a test runner with no
 * DOM, and only one of those three understands `import "./x.css"`. A string is
 * understood by all three, it is checked by the test in `test/adventure-ui.spec.ts`
 * (every custom property that is used is also defined), and `<GameTheme>` mounts
 * it exactly once.
 *
 * House rules encoded below, in case a future edit is tempted:
 *
 * - Nothing is a flat rectangle. Every surface is at least three layers: a
 *   material gradient, a bevel drawn with inset box-shadows, and a grain or
 *   noise overlay in a pseudo-element.
 * - `border-radius` is 0 or 1px. The classic's frames are mitred, not rounded.
 * - Pixel art is never scaled by a fraction and never smoothed. Every sprite
 *   goes through `fitSpriteBox`, which picks a whole multiple or a whole
 *   divisor and returns a whole-pixel box, and every sprite is drawn with
 *   `image-rendering: pixelated` in both directions.
 * - Every hairline is 1 *device* pixel, which is why the scale steps are coarse.
 */

import { RELIEF_CSS, WOOD_FIBRE, WOOD_FIGURE, reliefDecls } from "./relief.js";
import { deviceCapFloor } from "@game/render/text/glyphs";
import {
  BUNDLED_FONT_FACES, BUNDLED_FONT_FAMILY, FONTS, FONT_SIZES, METRICS, PALETTE, SCALE_STEPS, minDeviceTypePx, snap,
} from "./tokens.js";

const P = PALETTE;
const M = METRICS;

/**
 * Fractal noise as an inline SVG. It is the only "texture" in the build: an
 * image file would be another asset to ship and to cache-bust, and feTurbulence
 * is deterministic across browsers at this scale.
 */
const NOISE =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E"
  + "%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E"
  + "%3C/filter%3E%3Crect width='120' height='120' filter='url(%23n)'/%3E%3C/svg%3E\")";

/**
 * Long-grain walnut: the authored figure over a plank's own shading.
 *
 * This was one `repeating-linear-gradient` with irregularly spaced stops, on
 * the theory that irregular stops do not read as stripes. They do: the stop
 * pattern IS the period, and the period was 37px. Measured on the shipped town
 * screen's bottom bar (`.hs-tbar`, the widest wood in the build), on its
 * bare-wood rows y=938 and y=958 across x=900..1900, normalised
 * autocorrelation was 0.90 at lag 37 and 0.87 or better at every multiple out
 * to 296, on a row whose whole standard deviation is 8.2 grey levels. A 1000px
 * plank that repeats itself 26 times is ribbed velvet. The same rows now peak
 * at 0.25 over every lag from 8 to 300.
 *
 * The figure comes from `relief.ts` so the bar and the panels are cut from one
 * board, and the base gradient under it carries the colour the stops used to:
 * a plank is lighter along its top edge and darker at its foot, which is the
 * same light this kit's relief already assumes.
 */
const WOOD_GRAIN =
  `${WOOD_FIGURE}, ${WOOD_FIBRE},`
  + ` linear-gradient(177deg, ${P.wood3} 0%, ${P.wood2} 46%, ${P.wood1} 100%)`;

/** Mottled cut stone. Two offset radials read as chisel marks; a gradient alone does not. */
const STONE_FACE =
  `radial-gradient(120% 90% at 22% 0%, ${P.stone3} 0%, transparent 55%),`
  + ` radial-gradient(90% 120% at 85% 100%, ${P.stone0} 0%, transparent 60%),`
  + ` linear-gradient(178deg, ${P.stone2} 0%, ${P.stone1} 60%, ${P.stone0} 100%)`;

/** Aged parchment: warm in the middle, foxed at the corners. */
const PARCHMENT =
  `radial-gradient(120% 140% at 50% 20%, ${P.parch3} 0%, ${P.parch2} 58%, ${P.parch1} 100%)`;

/**
 * The type sizes for one step, as whole CSS pixels.
 *
 * Emitted per breakpoint rather than left as `calc(9px * var(--hm-scale))`,
 * because `calc` cannot round and every non-integer step put small text on a
 * fractional pixel. `snap` is the same function the metrics already use, so the
 * test that pins it now pins the type as well.
 */
const fontVars = (scale: number, indent: string, minHeight: number): string =>
  Object.entries(FONT_SIZES)
    // One per line, and indented: the stylesheet's own test finds a definition
    // with a line-anchored `^\s*--hm-…:`, so four declarations sharing a line
    // register as one and the other three read as used-but-undefined.
    //
    // Held to the step's type floor at 100% as well as under a zoom-out: the
    // floor is a cap height now (`minDeviceTypePx`), and at scale 1 the three
    // small tokens sit under it. The provider restates these from the device
    // height under a zoom-out (`zoomedOutScaleVars`), with the same floor.
    .map(([name, px]) => `${indent}--hm-fs-${name}: ${String(Math.max(snap(px, scale), minDeviceTypePx(minHeight)))}px;`)
    // The floor for a size a screen hard-codes in CSS pixels, written as
    // `font-size: max(11px, var(--hm-type-floor))`: `minDeviceTypePx` of the
    // step's own minimum height (10 at scale 1, 14 at the 1080p step), and
    // under a zoom-out restated by the theme provider from the device height
    // divided by the ratio, so an 11px label that reached the glass at 8.8
    // device px at 80% is held to the window's floor instead.
    .concat([
      `${indent}--hm-type-floor: ${String(minDeviceTypePx(minHeight))}px;`,
      // The bitmap cap floor for the step's window, unitless device rows (11
      // on a laptop, 15 at the 1080p step), for a layout whose width was
      // measured against the floor's readings and has to grow with them -
      // the adventure column reads it. Restated from the device height under
      // a zoom-out, like the rest.
      `${indent}--hm-cap-floor: ${String(deviceCapFloor(minHeight))};`,
    ])
    .join("\n");

const mediaSteps = SCALE_STEPS
  .filter((s) => s.minWidth > 0)
  .map((s) =>
    `@media (min-width: ${s.minWidth}px) and (min-height: ${s.minHeight}px) {\n`
    + `  .hm-root {\n    --hm-scale: ${s.scale};\n${fontVars(s.scale, "    ", s.minHeight)}\n  }\n}`)
  .join("\n");

/** Element id of the injected <svg> that carries `ALIAS_FILTER`. */
export const ALIAS_DEFS_ID = "hm-alias-defs";

/** The filter `.hm-alias` references. `components.tsx` mounts it. */
export const ALIAS_FILTER_ID = "hm-alias";

/**
 * The aliasing filter, as markup, so the provider can mount it once.
 *
 * `discrete` with three table values cuts alpha at 1/3 rather than at 1/2: a
 * serif at 15px carries hairlines and bracket serifs whose coverage never
 * reaches half a pixel, and a half cut deletes them - the stem of a `t` and the
 * thin of an `e` drop out and the word breaks up. At a third they survive and
 * come back one pixel wide, which is the weight a bitmap face draws them at.
 *
 * The filter region is pinned to the element's own box (`0 0 100% 100%` rather
 * than the default `-10% -10% 120% 120%`). A glow outside the box is the one
 * thing a hard cut turns into a slab, so it is clipped instead of quantised;
 * `text-shadow` lives inside the line box and survives.
 */
export const ALIAS_FILTER = `<svg id="${ALIAS_DEFS_ID}" width="0" height="0" `
  + `aria-hidden="true" focusable="false" `
  + `style="position:absolute;width:0;height:0;overflow:hidden">`
  + `<filter id="${ALIAS_FILTER_ID}" x="0" y="0" width="100%" height="100%" `
  + `color-interpolation-filters="sRGB">`
  + `<feComponentTransfer><feFuncA type="discrete" tableValues="0 1 1"/>`
  + `</feComponentTransfer></filter></svg>`;

/**
 * The bundled serif, one `@font-face` per weight and style. `font-display:
 * block` rather than `swap`: a screen that painted in the fallback and then
 * relaid out in the bundled face a frame later is exactly the flicker the
 * screenshot harness would catch as a defect, and the files are 25-29 KB.
 */
/**
 * Where the app is mounted, for the font URLs.
 *
 * The faces are authored as "/fonts/..." because the game normally owns its
 * origin. Served under a path they 404 and every screen silently falls back to
 * the system serif - a defect that looks like a font choice rather than a
 * missing file. Vite rewrites `import.meta.env.BASE_URL` at build time; the
 * guard is for the test runner, where `import.meta.env` does not exist.
 */
const BASE: string = ((): string => {
  try {
    return (import.meta as { env?: { BASE_URL?: string } }).env?.BASE_URL ?? "/";
  } catch {
    return "/";
  }
})();

const FONT_FACES = BUNDLED_FONT_FACES
  .map((f) => `@font-face {\n  font-family: "${BUNDLED_FONT_FAMILY}";\n  font-style: ${f.style};\n`
    + `  font-weight: ${String(f.weight)};\n  font-display: block;\n`
    + `  src: url("${BASE}${f.file.replace(/^\/+/, "")}") format("woff2");\n}`)
  .join("\n");

export const THEME_CSS = `
/* -------------------------------------------------------------- the serif */
${FONT_FACES}

/* ------------------------------------------------------------------ tokens */
.hm-root {
  --hm-scale: 1;
  /* Device pixels per CSS pixel while the browser is zoomed OUT, restated by
     the theme provider (0.8 at 80%); 1 at 100% and above. For the few
     metrics that hold a floor on the glass the way the type does. */
  --hm-dpr: 1;
  --hm-safe-top: env(safe-area-inset-top, 0px);
  --hm-safe-right: env(safe-area-inset-right, 0px);
  --hm-safe-bottom: env(safe-area-inset-bottom, 0px);
  --hm-safe-left: env(safe-area-inset-left, 0px);

  --hm-wood-0: ${P.wood0};
  --hm-wood-1: ${P.wood1};
  --hm-wood-2: ${P.wood2};
  --hm-wood-3: ${P.wood3};
  --hm-wood-4: ${P.wood4};
  --hm-wood-5: ${P.wood5};
  --hm-stone-0: ${P.stone0};
  --hm-stone-1: ${P.stone1};
  --hm-stone-2: ${P.stone2};
  --hm-stone-3: ${P.stone3};
  --hm-stone-4: ${P.stone4};
  --hm-gold-0: ${P.gold0};
  --hm-gold-1: ${P.gold1};
  --hm-gold-2: ${P.gold2};
  --hm-gold-3: ${P.gold3};
  --hm-gold-4: ${P.gold4};
  --hm-parch-1: ${P.parch1};
  --hm-parch-2: ${P.parch2};
  --hm-parch-3: ${P.parch3};

  --hm-ink: ${P.ink};
  --hm-text: ${P.text};
  --hm-text-dim: ${P.textDim};
  --hm-text-faint: ${P.textFaint};
  --hm-good: ${P.good};
  --hm-warn: ${P.warn};
  --hm-bad: ${P.bad};
  --hm-mana: ${P.mana};
  --hm-move: ${P.move};
  --hm-fog: ${P.fog};
  /* Default for a gauge with no kind modifier, so a bar is never invisible. */
  --hm-bar-colour: ${P.gold2};

  --hm-noise: ${NOISE};
  --hm-wood-grain: ${WOOD_GRAIN};
  --hm-stone-face: ${STONE_FACE};
  --hm-parchment: ${PARCHMENT};

  --hm-font-display: ${FONTS.display};
  --hm-font-body: ${FONTS.body};
  --hm-font-num: ${FONTS.numeric};

  /* Metrics resolve through the scale so a component never multiplies by hand. */
  --hm-u: calc(1px * var(--hm-scale));
  --hm-pad: calc(${M.panelPad}px * var(--hm-scale));
  --hm-gutter: calc(${M.gutter}px * var(--hm-scale));
  --hm-sidebar: calc(${M.sidebarWidth}px * var(--hm-scale));
  --hm-resbar-h: calc(${M.resourceBarHeight}px * var(--hm-scale));
  --hm-status-h: calc(${M.statusBarHeight}px * var(--hm-scale));
  --hm-minimap-inset: calc(${M.minimapInset}px * var(--hm-scale));

  /* Whole CSS pixels at every step; the media blocks below restate them
     snapped. See FONT_SIZES in tokens.ts for why these are not calc. */
${fontVars(1, "  ", 0)}

  /* A gilt hairline is one CSS pixel at every step - the steps are integers or
     exact halves, so it never lands between texels AT 100% ZOOM.
     
     It is not one *device* pixel. Browser zoom multiplies after the cascade, so
     at 90% this reaches the glass at 0.9px and the browser must decide what to
     do with the remainder. That is fine for a border, which the compositor can
     dither along its whole length, and it is not fine under a 10px numeral:
     hm-num and hm-label carried a HARD shadow at this offset and the
     smear filled the counters - 005 read as CO5 at 1680x1050 and 90%. Those
     two now use --hm-textshadow, which is blurred and degrades instead of
     blobbing. Anything reading this before adding a third hard shadow to small
     text: that is the failure mode. */
  --hm-hair: max(1px, calc(1px * var(--hm-scale) * 0.5));

  /* A shadow that survives a fractional device pixel.
     
     Blurred rather than hard, so a sub-pixel offset spreads instead of landing
     as a black line inside a glyph's counter. It costs a little of the crispness
     the hard version had at exactly 100% zoom, which is the right trade: the
     hard one was only correct at one zoom level and illegible at another. */
  --hm-textshadow: 0 1px 2px rgba(0, 0, 0, 0.6);

  color: var(--hm-text);
  font-family: var(--hm-font-body);
  font-size: var(--hm-fs-body);
  line-height: 1.25;

  /* THE TYPESET HALF IS ALIASED. See \`.hm-alias\` at the foot of this sheet.

     Three rounds looked for a declaration that hard-edges text on Linux and
     there is none: -webkit-font-smoothing is a macOS-Blink hint, and it shipped
     here for a round doing nothing at all. Measured on the shot it was supposed
     to have changed - battle.1080p, row y=20 through the serif Jorthas: 69, 84,
     146, 118, 71, 122, 177, 198, 140, a full grey ramp, against exactly two
     values (50 and 197) in the bitmap ATTACKER on the same 30px plate.

     What does work is an SVG alpha cut, and it is a filter rather than a font
     property, so it is applied per element and not inherited. */
}

${mediaSteps}

${RELIEF_CSS}

/* ------------------------------------------------------------------ resets */
.hm-root *, .hm-root *::before, .hm-root *::after { box-sizing: border-box; }
/* A reset, so it must carry no specificity: \`.hm-root button\` is (0,1,1) and
   silently beat every single-class button rule in the kit and in screens/,
   forcing \`color: inherit\` on them - a school tab on a parchment page came out
   as dark ink on dark wood. \`:where()\` contributes nothing, so one class wins. */
:where(.hm-root) :where(button) { font: inherit; color: inherit; }

/* ------------------------------------------------------------- the surfaces */

/* Grain and noise ride on ::before so a panel's own children stay above them. */
.hm-panel {
  position: relative;
  isolation: isolate;
${reliefDecls("chamfer", "wood", 2)}
}
.hm-panel::before {
  content: "";
  position: absolute;
  inset: 0;
  background-image: var(--hm-noise);
  background-size: calc(120px * var(--hm-scale));
  opacity: 0.05;
  mix-blend-mode: overlay;
  pointer-events: none;
  z-index: -1;
}

/* The knot: one soft radial that breaks the repeat of the grain. */
.hm-panel::after {
  content: "";
  position: absolute;
  inset: 0;
  background-image:
    radial-gradient(28% 42% at 18% 72%, rgba(0, 0, 0, 0.30) 0%, transparent 70%),
    radial-gradient(34% 26% at 76% 22%, rgba(0, 0, 0, 0.22) 0%, transparent 72%);
  pointer-events: none;
  z-index: -1;
}

.hm-panel--stone {
${reliefDecls("chamfer", "stone", 2)}
}
.hm-panel--parchment {
${reliefDecls("chamfer", "parchment", 2)}
  color: var(--hm-ink);
}
/* Flush: the same plate, seated in the surround rather than standing off it. */
.hm-panel--flush {
${reliefDecls("chamfer", "wood", 1)}
}

/* A gilt frame drawn as three rings: dark seat, gold bead, dark shadow line.
   Three box-shadows are cheaper and sharper than a border-image at every DPI. */
.hm-frame {
  position: relative;
  isolation: isolate;
  padding: var(--hm-pad);
${reliefDecls("recess", "wood", 1)}
}
.hm-frame--well {
${reliefDecls("recess", "stone", 2)}
}

/* --------------------------------------------------------------- the title */
.hm-title {
  font-family: var(--hm-font-display);
  font-size: var(--hm-fs-title);
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--hm-gold-4);
  text-shadow: 0 var(--hm-hair) 0 rgba(0, 0, 0, 0.85);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.hm-label {
  font-size: var(--hm-fs-small);
  letter-spacing: 0.04em;
  color: var(--hm-text-dim);
  /* Blurred, not a hard hairline - see --hm-textshadow. This is 11px type and
     a hard shadow under it fills the counters at any zoom that is not 100%. */
  text-shadow: var(--hm-textshadow);
}
.hm-num {
  font-family: var(--hm-font-num);
  font-variant-numeric: tabular-nums;
  font-feature-settings: "tnum" 1;
  color: var(--hm-text);
  /* The worst case of the hard shadow and the reason this token exists: a
     numeral has more enclosed counters than any letter, and 0, 5, 6, 8 and 9
     all close at the bottom where the shadow sat. At 1680x1050 and 90% zoom,
     005 read as CO5. */
  text-shadow: var(--hm-textshadow);
}

/* ------------------------------------------------- one medium, not two
   The build sets two kinds of type: hard-pixel runs stamped by BitmapText, and
   typeset runs in the serif stack. Three reviews in a row identified the pair
   sharing a plate - and often a ROW - as the tell that these screens are not
   the commercial game: "Elifell" stamped over "Level 1 - Knight" typeset,
   4px apart; "Jorthas" typeset beside "ATTACKER" stamped on one 30px plate.

   Neither half can move. Stamping everything gives up the lowercase running
   text a rules paragraph needs; typesetting everything gives up the count
   plaque. What can move is the MEDIUM: an alpha cut at a third of a pixel's
   coverage leaves the serif's own shapes and metrics exactly where they were
   and takes away the grey ramp along their edges, which is the whole of what a
   reader is seeing when the two look like different programs. Measured on the
   probe at 16.5px: the unfiltered run carries a 9-step ramp across a stem, the
   filtered run two values.

   It is applied to leaves that HOLD TEXT - \`:not(:has(*))\` for no element
   children, \`:not(:empty)\` for something inside - because a filter rasterises
   everything under it, and the cut is on ALPHA: an element that paints itself
   with a translucent layer, a gradient with rgba stops or a soft glow, has that
   layer quantised along with the type. The two negations are what keep the rule
   off the things that are ONLY paint. Measured over the six scenes: 63 sprite
   spans, 10 empty army sockets, the town screen's six aerial-haze overlays, the
   gauge fills, the pips and every hairline rule are empty elements and are not
   touched; \`.hm-icon\` in particular keeps the contact shadow
   \`.hm-relief__inlay\` gives it, which a second \`filter\` would have replaced.

   \`:where()\` keeps the whole selector at one class of specificity, so a
   screen's own colour rule still wins and any element can opt out with
   \`filter: none\` or \`.hm-alias--off\`. A run the generic rule does not reach -
   one drawn on a canvas, or a painted control - opts in with \`.hm-alias\`.

   The one text-bearing leaf in the six scenes that paints itself translucently
   is the hero sheet's mastery rung, which is excused below. */
.hm-alias,
:where(.hm-root) :where(span, small, em, strong, b, i, label, th, td, dt, dd, p, div, h1, h2, h3, h4, h5, h6, li, figcaption, legend, caption, output):not(:has(*)):not(:empty):not(.hm-alias--off) {
  filter: url(#${ALIAS_FILTER_ID});
}
/* The cut is on ALPHA, so a blurred shadow under an aliased run comes back as
   a hard one at the same offset - which is what this face wants anyway. It is
   not left to chance: a 2px blur cut at a third is 2px of solid black, and
   under 10px type that is the counter-filling failure --hm-textshadow exists
   to avoid. One pixel, no blur. */
.hm-alias,
:where(.hm-root) :where(span, small, em, strong, b, i, label, th, td, dt, dd, p, div, h1, h2, h3, h4, h5, h6, li, figcaption, legend, caption, output):not(:has(*)):not(:empty):not(.hm-alias--off) {
  --hm-textshadow: 0 var(--hm-hair) 0 rgba(0, 0, 0, 0.72);
}

/* --------------------------------------------- clearance a plate must keep
   Two rules that reach into screens/ from the kit, because what they are
   fixing is the kit's own metric leaking into a screen's fixed box.

   The hero sheet's secondary-skill plate is a grid cell of a fixed height
   (\`grid-auto-rows: minmax(0, 1fr)\`) holding a caption, a 96px icon socket and
   a three-rung mastery rail. Measured live at 1920x1080 the socket's foot
   landed 10.5px below the rail's head, so the icon covered BASIC outright and
   ADVANCED read "ANCED" on all eight plates - a word a reader cannot read, in
   the middle of the sheet they read most. Four of those pixels are this kit's:
   a caption set in the text face now carries its own descender INSIDE its box
   (see bandHeightOf in bitmapText.tsx) and "Air Magic" descends. The other six
   predate it.

   The plate has the room; it was spending it on air. 9px of gap twice and 10px
   under the rail is 28px of white space around 126px of content, so the gaps
   pay for the clearance and nothing is cropped or shrunk. The rail is also
   lifted out of the paint order, so that if a future caption grows again the
   thing that survives is the WORD and not the picture.

   The right home for the first rule is HeroScreen's own SKILL_ICON, which this
   workstream does not own. */
.hs-hero:not(.hs-hero--compact) .hs-skill { gap: 4px; padding-bottom: 7px; }
.hs-hero .hm-hs__track { position: relative; z-index: 1; }
/* The rung paints itself with a translucent black ramp - rgba 0.42 down to
   rgba 0.16 - and an alpha cut turns the top half of that ramp opaque and the
   bottom half clear, i.e. a hard black band across the unlit rungs. It is the
   one text-bearing leaf in the six scenes in that state. */
.hs-hero .hm-hs__rung { filter: none; }

/* ------------------------------------------------------------------ button */
.hm-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: calc(var(--hm-u) * 4);
  padding: calc(var(--hm-u) * 4) calc(var(--hm-u) * 10);
  border: var(--hm-hair) solid var(--hm-gold-0);
  border-radius: 1px;
  cursor: pointer;
  font-family: var(--hm-font-display);
  font-size: var(--hm-fs-small);
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: var(--hm-gold-4);
  text-shadow: 0 var(--hm-hair) 0 rgba(0, 0, 0, 0.9);
${reliefDecls("chamfer", "wood", 2)}
}
.hm-btn:hover:not(:disabled) { filter: brightness(1.16); color: #fff3d2; }
/* Pressed: the plate is below the surface now, so the relief inverts. There is
   no second recipe for that - it is the recess at the same material and depth. */
.hm-btn:active:not(:disabled) {
${reliefDecls("recess", "wood", 2)}
}
.hm-btn:focus-visible {
  outline: calc(var(--hm-hair) * 2) solid var(--hm-gold-3);
  outline-offset: calc(var(--hm-hair) * 1);
}
.hm-btn:disabled {
  cursor: default;
  color: var(--hm-text-faint);
  filter: saturate(0.4) brightness(0.72);
}
.hm-btn--primary {
${reliefDecls("chamfer", "bronze", 3)}
  color: #1d1405;
  text-shadow: 0 var(--hm-hair) 0 rgba(255, 240, 200, 0.5);
}
.hm-btn--primary:hover:not(:disabled) { filter: brightness(1.14); color: #1d1405; }
.hm-btn--icon { padding: calc(var(--hm-u) * 3); }

/* -------------------------------------------------------------------- slot */
/* A recessed socket. Contents sit *in* it, so the inner shadow is on top. */
.hm-slot {
  position: relative;
  isolation: isolate;
  display: block;
  padding: 0;
${reliefDecls("recess", "stone", 2)}
}
.hm-slot--button { cursor: pointer; }
.hm-slot--button:hover { filter: brightness(1.25); }
.hm-slot--selected {
  outline: calc(var(--hm-hair) * 2) solid var(--hm-gold-3);
  outline-offset: calc(var(--hm-hair) * -2);
}
/* An empty socket is the SAME cut as a full one - dimming it turns the recess
   back into the flat hole this primitive exists to stop. */
.hm-slot--empty { opacity: 1; }

/* ----------------------------------------------------------------- divider */
/* Seated groove with a gold bead: dark line, gold line, light line. */
.hm-divider {
  height: calc(var(--hm-hair) * 3);
  margin: var(--hm-gutter) 0;
  background-image: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0.75) 0%, rgba(0, 0, 0, 0.75) 33%,
    var(--hm-gold-1) 33%, var(--hm-gold-1) 66%,
    rgba(255, 226, 170, 0.28) 66%, rgba(255, 226, 170, 0.28) 100%);
}
.hm-divider--vertical {
  width: calc(var(--hm-hair) * 3);
  height: auto;
  align-self: stretch;
  margin: 0 var(--hm-gutter);
  background-image: linear-gradient(
    90deg,
    rgba(0, 0, 0, 0.75) 0%, rgba(0, 0, 0, 0.75) 33%,
    var(--hm-gold-1) 33%, var(--hm-gold-1) 66%,
    rgba(255, 226, 170, 0.28) 66%, rgba(255, 226, 170, 0.28) 100%);
}

/* ---------------------------------------------------------------- statline */
/* label ................ value, with a leader that never wraps or clips. */
.hm-statline {
  display: flex;
  align-items: baseline;
  gap: calc(var(--hm-u) * 4);
  min-width: 0;
  padding: calc(var(--hm-u) * 1) 0;
}
.hm-statline__label {
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.hm-statline__leader {
  flex: 1 1 auto;
  min-width: calc(var(--hm-u) * 6);
  height: 1px;
  transform: translateY(calc(var(--hm-u) * -2));
  background-image: linear-gradient(90deg, rgba(0, 0, 0, 0.55) 50%, rgba(255, 226, 170, 0.14) 50%);
  background-size: calc(var(--hm-u) * 4) 1px;
}
.hm-statline__value { flex: 0 0 auto; white-space: nowrap; }

/* --------------------------------------------------------------- gauge bar */
.hm-bar {
  position: relative;
  height: calc(var(--hm-u) * 6);
${reliefDecls("recess", "stone", 1)}
  overflow: hidden;
}
.hm-bar__fill {
  height: 100%;
  background-image:
    linear-gradient(180deg, rgba(255, 255, 255, 0.35) 0%, rgba(255, 255, 255, 0) 45%, rgba(0, 0, 0, 0.3) 100%),
    linear-gradient(90deg, var(--hm-bar-colour) 0%, var(--hm-bar-colour) 100%);
  box-shadow: inset calc(var(--hm-hair) * -1) 0 0 rgba(0, 0, 0, 0.5);
  transition: width 120ms linear;
}
.hm-bar--move { --hm-bar-colour: var(--hm-move); }
.hm-bar--mana { --hm-bar-colour: var(--hm-mana); }

/* ------------------------------------------------------------ atlas sprite */
/* One <span> per frame. background-position is negative-offset into the
   sheet; the sheet is never resized, so the sample grid stays whole. */
.hm-icon {
  display: inline-block;
  flex: 0 0 auto;
  background-repeat: no-repeat;
  vertical-align: middle;
}
/* Never smoothed, in either direction. The box filter was here for downscales
   on the theory that dropping three of every four source pixels loses detail -
   it does, and that is what a nearest-neighbour downscale IS. What the filter
   did instead was invent a new colour for almost every destination pixel:
   measured on the shipped adventure sidebar, the spearwarden socket carried 1,009
   unique colours in 1,700 px (0.59) against 0.018 for the same class of sprite
   drawn by the world renderer. A panel sprite that reads as a JPEG next to a
   crisp one on the map is the loudest "this is not a pixel game" tell there is,
   and detail is bought back with a bigger socket, not with a ramp. */
.hm-icon--up,
.hm-icon--down { image-rendering: pixelated; }
.hm-icon--missing {
  background-image: repeating-linear-gradient(45deg, ${P.bad} 0 3px, ${P.stone0} 3px 6px);
  opacity: 0.6;
}

/* --------------------------------------------------------------- scrolling */
.hm-scroll { overflow-y: auto; overflow-x: hidden; scrollbar-width: thin;
  scrollbar-color: var(--hm-gold-1) var(--hm-stone-0); }
.hm-scroll::-webkit-scrollbar { width: calc(var(--hm-u) * 8); }
.hm-scroll::-webkit-scrollbar-track { background: var(--hm-stone-0);
  box-shadow: inset 0 0 0 var(--hm-hair) rgba(0, 0, 0, 0.8); }
.hm-scroll::-webkit-scrollbar-thumb {
  background-image: linear-gradient(180deg, var(--hm-gold-2) 0%, var(--hm-gold-1) 100%);
  box-shadow: inset 0 0 0 var(--hm-hair) rgba(0, 0, 0, 0.6);
}
/* A panel that has more below says so.
   ScrollArea shortens its viewport to the last WHOLE row rather than draw a
   torn one, and a panel that simply stops carrying its last row reads as a
   panel with nothing in it - the towns roster at 1366x768 was read as "the
   town has no type" rather than as "scroll". So a folded container carries a
   gilt chevron over a shading, just above the fold line the effect measured
   into --hm-fold.

   It is a STICKY PSEUDO-ELEMENT and not the container's own background, and
   that is the whole of it: a background paints BEHIND every child, and the
   panels this fires on - the adventure sheet, the towns roster - fill their
   scroll box with opaque wood rows, so the chevron was painted and then
   covered. Measured on adventure-night at 1920x1080 zoom 100% (the folded
   case: 266px of content in a 259px box, --hm-fold 31.5px): with the
   background layers the fold region was bare wood, no chevron pixel anywhere
   in it; with this rule the chevron stands on the fold line. position:
   sticky pins it to the bottom of the scroll VIEWPORT rather than the bottom
   of the content, which is what background-attachment: local could not do,
   and margin-top: -11px gives it back the eleven pixels it takes, so
   scrollHeight is what it was and the ResizeObserver in ScrollArea cannot
   chase its own tail. bottom sits it one pixel inside the fold so the
   clip-path cannot eat its keyline. */
.hm-scroll[data-folded] {
  --hm-fold: 0px;
}
.hm-scroll[data-folded]::after {
  content: "";
  display: block;
  position: sticky;
  bottom: calc(var(--hm-fold) - 1px);
  height: 11px;
  margin-top: -11px;
  pointer-events: none;
  background-image:
    url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='13' height='7'%3E%3Cpath d='M1 1h11L6.5 6z' fill='%23f0d79a' stroke='%230a0704' stroke-width='1.2' stroke-linejoin='round'/%3E%3C/svg%3E"),
    linear-gradient(180deg, rgba(10, 7, 4, 0) 0%, rgba(10, 7, 4, 0.62) 100%);
  background-repeat: no-repeat, no-repeat;
  background-position: center bottom 2px, center bottom;
  background-size: 13px 7px, 100% 11px;
}

/* --------------------------------------------------------------- utilities */
.hm-row { display: flex; align-items: center; gap: var(--hm-gutter); min-width: 0; }
.hm-col { display: flex; flex-direction: column; gap: var(--hm-gutter); min-width: 0; }
.hm-grow { flex: 1 1 auto; min-width: 0; min-height: 0; }
.hm-clip { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; min-width: 0; }
.hm-hidden { position: absolute; width: 1px; height: 1px; overflow: hidden; clip-path: inset(50%); }

/* ------------------------------------------------- the adventure-screen grid */
/* The canvas is a *sibling* of the panels, not a child (D8): each lives in its
   own grid area, the areas do not overlap, and so nothing ever needs to call
   stopPropagation to decide who owns a pointer. */
.hm-adventure {
  display: grid;
  grid-template-columns: minmax(0, 1fr) var(--hm-sidebar);
  grid-template-rows: minmax(0, 1fr) var(--hm-status-h) var(--hm-resbar-h);
  grid-template-areas:
    "map  side"
    "status side"
    "res  side";
  gap: var(--hm-gutter);
  width: 100%;
  height: 100%;
  padding: var(--hm-gutter);
  background-color: var(--hm-stone-0);
  background-image: var(--hm-stone-face);
  overflow: hidden;
}
.hm-adventure__map {
  grid-area: map;
  position: relative;
  overflow: hidden;
  min-width: 0;
  min-height: 0;
}
/* The world canvas fills its area and keeps its own pixel grid. Only the canvas
   is styled here - the renderer may also mount overlay layers of its own, and
   forcing 100% on every child would break them. */
.hm-adventure__map > canvas {
  display: block;
  width: 100%;
  height: 100%;
  image-rendering: pixelated;
}
.hm-adventure__status { grid-area: status; min-width: 0; }
.hm-adventure__res { grid-area: res; min-width: 0; }
.hm-adventure__side {
  grid-area: side;
  display: flex;
  flex-direction: column;
  gap: var(--hm-gutter);
  min-height: 0;
  min-width: 0;
}
/* The sidebar has to survive 690px of usable height at 1366x768, so the three
   lists below the minimap are told explicitly who yields first: the hero strip
   takes the slack, the town strip is capped and scrolls, the info sheet keeps
   its intrinsic height because a clipped stat line is worse than a short list. */
.hm-side-fixed { flex: 0 0 auto; }
.hm-side-grow { flex: 1 1 0; min-height: calc(var(--hm-u) * 64); }
.hm-side-cap { flex: 0 1 auto; min-height: calc(var(--hm-u) * 48); max-height: 26%; }
.hm-side-sheet { flex: 0 1 auto; min-height: calc(var(--hm-u) * 110); max-height: 46%; overflow: hidden; }

/* The date has two forms; swapping them by width beats a resize listener. */
.hm-date-short { display: inline; }
.hm-date-long { display: none; }
@media (min-width: 1180px) {
  .hm-date-short { display: none; }
  .hm-date-long { display: inline; }
}

/* A row in the hero or town strip. */
.hm-entity-row {
  display: flex;
  align-items: center;
  gap: calc(var(--hm-u) * 5);
  width: 100%;
  padding: calc(var(--hm-u) * 3);
  border: 0;
  text-align: left;
  cursor: pointer;
  background-color: rgba(0, 0, 0, 0.18);
  box-shadow: inset 0 0 0 var(--hm-hair) rgba(0, 0, 0, 0.5);
}
.hm-entity-row:hover { background-color: rgba(217, 178, 90, 0.10); }
.hm-entity-row--selected {
  background-color: rgba(217, 178, 90, 0.16);
  box-shadow:
    inset 0 0 0 var(--hm-hair) var(--hm-gold-2),
    inset 0 0 calc(var(--hm-u) * 10) rgba(217, 178, 90, 0.25);
}
.hm-entity-row:focus-visible { outline: calc(var(--hm-hair) * 2) solid var(--hm-gold-3); outline-offset: calc(var(--hm-hair) * -2); }

/* The owner pip beside a town or hero name. */
.hm-pip {
  width: calc(var(--hm-u) * 7);
  height: calc(var(--hm-u) * 7);
  flex: 0 0 auto;
  border-radius: 50%;
  box-shadow:
    inset 0 0 0 var(--hm-hair) rgba(0, 0, 0, 0.8),
    0 0 calc(var(--hm-u) * 3) rgba(0, 0, 0, 0.6);
}

/* The minimap canvas: never smoothed, never fractional. */
.hm-minimap {
  display: block;
  image-rendering: pixelated;
  cursor: crosshair;
  width: 100%;
  height: 100%;
}

/* The status line. Parchment under one line of ink, so an illegality reads as
   a note pinned to the board rather than as an error toast. */
.hm-status {
  display: flex;
  align-items: center;
  gap: calc(var(--hm-u) * 6);
  padding: 0 calc(var(--hm-u) * 6);
  height: 100%;
  font-size: var(--hm-fs-small);
  white-space: nowrap;
  overflow: hidden;
}
.hm-status--illegal { color: #5c1b12; }
.hm-status__badge {
  flex: 0 0 auto;
  font-family: var(--hm-font-num);
  font-size: var(--hm-fs-tiny);
  letter-spacing: 0.08em;
  padding: 0 calc(var(--hm-u) * 4);
  color: var(--hm-parch-3);
  background-color: rgba(92, 27, 18, 0.85);
  box-shadow: inset 0 0 0 var(--hm-hair) rgba(0, 0, 0, 0.6);
}

/* ----------------------------------------------- landscape-phone overlay */
/* Portrait phones get a veil rather than a crushed layout. The game is
   authored for a wide 16:9 frame; standing the phone up is not a viewport
   we ship, it is a prompt to turn it. */
.hm-rotate {
  position: fixed;
  inset: 0;
  z-index: 80;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(6, 8, 12, 0.92);
  padding: var(--hm-safe-top) var(--hm-safe-right) var(--hm-safe-bottom) var(--hm-safe-left);
}
.hm-rotate__card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 22px 28px;
  text-align: center;
${reliefDecls("chamfer", "wood", 2)}
}
.hm-rotate__note { color: var(--hm-text-dim); }

/* Town / hero / spellbook: scroll the desktop compact sheet rather than
   clip it. A full redesign of those screens is a later pass; this is the
   overlay that lets them be used on ~800x390. Screens that opt into
   hs-screen--phone-fit scale the plate instead of scrolling it. */
.hm-root--phone .hs-screen {
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  padding-top: var(--hm-safe-top);
  padding-bottom: var(--hm-safe-bottom);
}
.hm-root--phone .hs-screen__body {
  overflow: auto;
  -webkit-overflow-scrolling: touch;
}
.hs-phone-sheet {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
}
.hs-screen--phone-fit {
  overflow: hidden;
  --hs-phone-scale: 0.5;
}
.hm-root--phone .hs-screen--phone-fit { overflow: hidden; }
.hs-screen--phone-fit .hs-phone-sheet {
  /* Above the opaque Save/Settings veil: a transform makes this a stacking
     context, and without a z-index the veil (z-index 1) paints over it. */
  position: relative;
  z-index: 2;
  flex: 0 0 auto;
  width: max-content;
  min-width: 0;
  height: auto;
  min-height: min-content;
  overflow: visible;
  transform: translateX(var(--hs-phone-shift-x, 0px)) scale(var(--hs-phone-scale));
  transform-origin: top left;
}
.hm-root--phone .hs-screen--phone-fit .hs-screen__body,
.hs-screen--phone-fit .hs-screen__body,
.hm-root--phone .hs-screen--phone-fit .hs-book {
  overflow: visible;
  height: auto;
  min-height: min-content;
  flex: 0 0 auto;
}
.hm-root--phone button { touch-action: manipulation; }

/* Phone adventure: one cell, full-bleed map. The desktop grid keeps a
   resource-bar row and a sidebar track even when those panels are not
   mounted; hiding the overlay keys then uncovered empty stone, not more
   world. Force a single map area so the canvas actually grows. */
.hm-root--phone .hm-adventure {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: minmax(0, 1fr);
  grid-template-areas: "map";
  gap: 0;
  padding: 0;
  --hm-sidebar: 0px;
  --hm-status-h: 0px;
  --hm-resbar-h: 0px;
}
.hm-root--phone .hm-adventure__map {
  grid-area: map;
  position: relative;
  inset: auto;
  width: 100%;
  height: 100%;
  margin: 0;
}
.hm-root--phone .hm-phone {
  grid-area: map;
  position: absolute;
  inset: 0;
  z-index: 4;
}
`;

/** Element id of the injected <style>, so a host can find or replace it. */
export const THEME_STYLE_ID = "hm-theme";
